/* ============================================================================
   site-gate.js - public landing + optional test-only signup lock
   ----------------------------------------------------------------------------
   Landing (home.html) is public. App pages still require a signed-in user.

   While TEST_SIGNUP_ENABLED is true, Create account needs an invite code checked
   server-side (SIGNUP_INVITE_CODE / verifySignupInvite). Logins are unchanged.

   Flip TEST_SIGNUP_ENABLED to false when open registration is ready.
   PREVIEW_MODE (coming-soon gate) is off so the landing is reachable.
   ============================================================================ */

import { functions, isConfigured } from "./firebase-config.js?v=20261007m";
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";

/** Coming-soon gate. Keep false while the landing should be public. */
export const PREVIEW_MODE = false;

/** Legacy preview link key (only used if PREVIEW_MODE is turned back on). */
export const PREVIEW_ACCESS_KEY = "jar-vault-preview";

/**
 * Test / beta signup lock. The invite secret is NOT shipped in the client  - 
 * it lives in Cloud Functions param SIGNUP_INVITE_CODE.
 */
export const TEST_SIGNUP_ENABLED = false;

const STORAGE_KEY = "ccj_preview_access";

export function grantAccessFromUrl() {
  if (!PREVIEW_MODE) return true;
  const param = new URLSearchParams(location.search).get("access");
  if (param && param === PREVIEW_ACCESS_KEY) {
    localStorage.setItem(STORAGE_KEY, PREVIEW_ACCESS_KEY);
    return true;
  }
  return false;
}

export function hasPreviewAccess() {
  if (!PREVIEW_MODE) return true;
  const host = location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  return localStorage.getItem(STORAGE_KEY) === PREVIEW_ACCESS_KEY;
}

export function previewEntryUrl(origin = location.origin) {
  return `${origin}/?access=${PREVIEW_ACCESS_KEY}`;
}

export function clearPreviewAccess() {
  localStorage.removeItem(STORAGE_KEY);
}

/** True when the invite password matches (or test signup is disabled). */
export async function checkTestSignupPassword(value) {
  if (!TEST_SIGNUP_ENABLED) return { ok: true };
  const entered = String(value || "").trim();
  if (!entered) {
    return { ok: false, message: "Enter the test access password to create an account." };
  }
  if (!isConfigured || !functions) {
    return { ok: false, message: "Signup invite check needs the live site." };
  }
  try {
    const callable = httpsCallable(functions, "verifySignupInvite");
    await callable({ code: entered });
    return { ok: true };
  } catch (err) {
    const msg =
      err?.message?.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") ||
      "That test access password isn’t right.";
    return { ok: false, message: msg };
  }
}
